import * as THREE from 'three';
import { Kit, place } from '../seasons/kit.ts';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * Musgoviejo's lower street, drawn house by house (docs/sendas/clerigo.md, chapter 1): the fronts along the lane are
 * five kinds (timber over plaster, rubble stone with dressed corners, a tall gable to the street with its hoist, a
 * workshop with its awning and sign, a jettied upper storey on corbels), each with its door and step, shutters of its
 * own colour, snow on the sills and icicles under the eaves; behind the fronts the backs step down into yards (a well,
 * a bare tree, a haystack, a woodshed) walled in dry stone; here and there a front gives way to a little square (a
 * bench, a tree, a wayside cross); the village's arch at the top is a real arch in a stretch of the old wall.
 *
 * All of it goes into the layer's one merged mesh (seasons/kit.ts): no draw call of its own. Kept to boxes, a few
 * prisms and cones (a house front is about forty pieces; the rows behind are cheaper, and the yards cheaper still).
 */

const S = WORLD_PER_HEX;
const PLASTER = ['#857f76', '#76797d', '#8a8580', '#6d7378', '#7e746c'];
const TIMBER = ['#261f1a', '#30281f', '#1f1a16', '#2b2420'];
const STONE = ['#6c7079', '#61656e', '#767a83', '#5a5e66'];
const QUOIN = '#8d929b';
const SNOW = ['#c8d3e1', '#bccadb', '#d3dcea'];
const ICE = '#dfe9f7';
const ROOF = ['#2e333b', '#3a3129', '#353b44', '#2b2622', '#40464f', '#3b2c28'];
/** Shutters and doors: each house its own (moss green, oxblood, dark blue, weathered brown, slate). */
const PAINT = ['#2f4038', '#5a2a24', '#273448', '#3d2c22', '#3a3f47'];
const DARK = '#15120f';

export interface StreetCtx {
  hex(q: number, r: number, h?: number): THREE.Vector3;
  k(q: number, r: number, salt?: number): number;
  light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick?: number): void;
}

const COLD = new THREE.Color('#cfdcff');
const WARM = new THREE.Color('#ffc89a');
const WHITE = new THREE.Color('#f4f8ff');

/** A frame on a cell: x along the front (to the house's right), z out of the front, y up from the ground. */
class Frame {
  fx: number;
  fz: number;
  sx: number;
  sz: number;
  constructor(
    readonly p: THREE.Vector3,
    readonly yaw: number,
  ) {
    this.fx = Math.sin(yaw);
    this.fz = Math.cos(yaw);
    this.sx = Math.cos(yaw);
    this.sz = -Math.sin(yaw);
  }
  at(x: number, y: number, z: number): THREE.Vector3 {
    return new THREE.Vector3(this.p.x + this.sx * x + this.fx * z, this.p.y + y, this.p.z + this.sz * x + this.fz * z);
  }
  m(x: number, y: number, z: number, s: THREE.Vector3 | number, tx = 0, tz = 0, yaw = 0): THREE.Matrix4 {
    const w = this.at(x, y, z);
    return place(w.x, w.y, w.z, this.yaw + yaw, s, tx, tz);
  }
}

const v = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

export class StreetCraft {
  private box = new THREE.BoxGeometry(1, 1, 1);
  /** A triangular prism: base 1 wide on y = 0, apex at y = 1, 1 deep (z from −0.5 to 0.5). */
  private prism: THREE.BufferGeometry;
  private cone = new THREE.ConeGeometry(0.5, 1, 5);
  private cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
  private mound = new THREE.SphereGeometry(0.5, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2);

  constructor(
    private kit: Kit,
    private c: StreetCtx,
  ) {
    const sh = new THREE.Shape();
    sh.moveTo(-0.5, 0);
    sh.lineTo(0.5, 0);
    sh.lineTo(0, 1);
    sh.closePath();
    this.prism = new THREE.ExtrudeGeometry(sh, { depth: 1, bevelEnabled: false });
    this.prism.translate(0, 0, -0.5);
  }

  dispose(): void {
    for (const g of [this.box, this.prism, this.cone, this.cyl, this.mound]) g.dispose();
  }

  private b(F: Frame, x: number, y: number, z: number, w: number, h: number, d: number, col: string, tx = 0, tz = 0): void {
    this.kit.add(this.box, col, F.m(x, y, z, v(w, h, d), tx, tz));
  }

  /** Icicles hanging from an edge (from x0 to x1 at height y, depth z). */
  private icicles(F: Frame, x0: number, x1: number, y: number, z: number, n: number, k: number): void {
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const len = 0.08 + (((k * 97 + i * 31) % 10) / 10) * 0.16;
      this.kit.add(this.cone, ICE, F.m(x0 + (x1 - x0) * t, y - len / 2, z, v(0.045, len, 0.045), Math.PI));
    }
  }

  /** A shuttered window: the dark pane (or a lit one), its two leaves open or shut, a sill with snow. */
  private window(F: Frame, x: number, y: number, z: number, w: number, h: number, paint: string, open: boolean, lit: string | null): void {
    this.b(F, x, y, z + 0.005, w, h, 0.03, lit ?? DARK);
    if (open)
      for (const s0 of [-1, 1]) this.b(F, x + s0 * (w * 0.5 + w * 0.26), y, z + 0.02, w * 0.48, h * 1.02, 0.03, paint);
    else {
      for (const s0 of [-1, 1]) this.b(F, x + s0 * w * 0.25, y, z + 0.02, w * 0.47, h, 0.03, paint);
      if (lit) this.b(F, x, y, z + 0.03, 0.02, h * 0.9, 0.02, lit);
    }
    this.b(F, x, y - h * 0.5 - 0.025, z + 0.04, w * 1.3, 0.05, 0.1, '#55595f');
    this.b(F, x, y - h * 0.5 + 0.01, z + 0.045, w * 1.24, 0.035, 0.09, SNOW[0] as string);
  }

  /** A door with its frame and a step in front (snow on the step), the lintel in stone or timber. */
  private door(F: Frame, x: number, z: number, w: number, h: number, paint: string, frame: string, arched: boolean): void {
    this.b(F, x, 0.3 + h * 0.5, z + 0.01, w, h, 0.03, paint);
    this.b(F, x, 0.3 + h * 0.5, z + 0.025, w * 0.08, h * 0.9, 0.02, DARK);
    for (const s0 of [-1, 1]) this.b(F, x + s0 * (w * 0.5 + 0.03), 0.3 + h * 0.5, z + 0.02, 0.06, h + 0.04, 0.05, frame);
    if (arched) this.kit.add(this.prism, frame, F.m(x, 0.3 + h, z + 0.02, v(w + 0.12, 0.14, 0.05)));
    else this.b(F, x, 0.3 + h + 0.03, z + 0.03, w + 0.16, 0.07, 0.07, frame);
    this.b(F, x, 0.08, z + 0.14, w + 0.16, 0.16, 0.26, '#5b6068');
    this.b(F, x, 0.17, z + 0.14, w + 0.12, 0.03, 0.22, SNOW[1] as string);
  }

  /** A roof with its ridge along the front (eaves to the street) or across it (the gable to the street). */
  private roof(F: Frame, w: number, d: number, y: number, k: number, gable: boolean, wall: string, snowy: number): { eaveY: number; eaveZ: number } {
    const pitch = gable ? 0.72 : 0.62;
    const col = ROOF[Math.floor(k * 5.99)] as string;
    const span = gable ? w : d;
    const len = gable ? d + 0.2 : w + 0.22;
    const half = span * 0.5 + 0.1;
    const slab = half / Math.cos(pitch);
    const rise = Math.tan(pitch) * span * 0.5;
    for (const s0 of [-1, 1]) {
      const off = (span * 0.25 + 0.05) * s0;
      const cy = y + rise * 0.5 + 0.02;
      if (gable) {
        this.b(F, off, cy, 0, slab, 0.08, len, col, 0, -s0 * pitch);
        // Snow over the slates (thick on the lee side, a skin on the other).
        const cover = s0 > 0 ? snowy : snowy * 0.6;
        if (cover > 0.15) this.b(F, off * (1 - cover * 0.4), cy + 0.06, 0, slab * cover, 0.06, len - 0.04, SNOW[Math.floor(k * 2.99)] as string, 0, -s0 * pitch);
      } else {
        this.b(F, 0, cy, off, len, 0.08, slab, col, s0 * pitch, 0);
        const cover = s0 < 0 ? snowy : snowy * 0.6;
        if (cover > 0.15) this.b(F, 0, cy + 0.06, off * (1 - cover * 0.4), len - 0.04, 0.06, slab * cover, SNOW[Math.floor(k * 2.99)] as string, s0 * pitch, 0);
      }
    }
    // The ridge, snowed on.
    if (gable) this.b(F, 0, y + rise + 0.06, 0, 0.16, 0.08, len, SNOW[0] as string);
    else this.b(F, 0, y + rise + 0.06, 0, len, 0.08, 0.16, SNOW[0] as string);
    // The gable ends (plaster or stone).
    if (gable) {
      this.kit.add(this.prism, wall, F.m(0, y, d * 0.5 - 0.02, v(w, rise, 0.06)));
      this.kit.add(this.prism, wall, F.m(0, y, -d * 0.5 + 0.02, v(w, rise, 0.06)));
      // The bargeboards along the gable's edge, dark.
      for (const s0 of [-1, 1]) this.b(F, s0 * w * 0.26, y + rise * 0.5, d * 0.5 + 0.08, slab, 0.06, 0.05, TIMBER[0] as string, 0, -s0 * pitch);
      return { eaveY: y, eaveZ: d * 0.5 + 0.1 };
    }
    // (Along the front the gables face the sides: the caller adds them, turned a quarter, with sideGables.)
    return { eaveY: y - 0.03, eaveZ: d * 0.5 + 0.1 };
  }

  /** The gable ends for a roof with its ridge along the front: prisms turned to face the sides. */
  private sideGables(F: Frame, w: number, d: number, y: number, rise: number, wall: string): void {
    for (const s0 of [-1, 1]) {
      const p = F.at(s0 * (w * 0.5 - 0.02), y, 0);
      this.kit.add(this.prism, wall, place(p.x, p.y, p.z, F.yaw + Math.PI / 2, v(d, rise, 0.06)));
    }
  }

  /**
   * A house front on the lane (its face toward `yaw`): `kind` 0–4 (timber, stone, gable, workshop, jettied); `storeys`
   * 1–2; `lit` a window lit behind its shutters (cold, or warm now and then: someone still awake).
   */
  front(p: THREE.Vector3, yaw: number, k: number, k2: number, storeys: number, lit: boolean): void {
    const F = new Frame(p, yaw);
    const kind = Math.floor(k2 * 4.999);
    const w = S * (0.98 + k * 0.1);
    const d = S * 1.0;
    const H = 0.55 + storeys * 0.52 + (kind === 2 ? 0.2 : 0);
    const plaster = PLASTER[Math.floor(k * 4.99)] as string;
    const stone = STONE[Math.floor((k * 7) % 3.99)] as string;
    const tb = TIMBER[Math.floor((k * 11) % 3.99)] as string;
    const paint = PAINT[Math.floor((k * 13 + k2 * 5) % 4.99)] as string;
    const wall = kind === 1 ? stone : plaster;
    const fz = d * 0.5;
    // Footing and walls.
    this.b(F, 0, 0.15, 0, w + 0.08, 0.3, d + 0.08, '#474b52');
    this.b(F, 0, 0.3, fz + 0.03, w + 0.1, 0.05, 0.12, SNOW[2] as string);
    if (kind === 4) {
      // Jettied: the ground floor of stone, the upper storey sticks out over the street on corbels.
      const g = 0.62;
      this.b(F, 0, 0.3 + g * 0.5, 0, w, g, d, stone);
      this.b(F, 0, 0.3 + g + (H - g) * 0.5, 0.1, w + 0.04, H - g, d + 0.2, plaster);
      for (const x of [-0.42, 0, 0.42]) this.b(F, x * w, 0.3 + g - 0.06, fz + 0.06, 0.08, 0.12, 0.14, tb);
      this.b(F, 0, 0.3 + g + 0.02, fz + 0.2, w + 0.06, 0.06, 0.04, tb);
      this.b(F, 0, 0.3 + H - 0.04, fz + 0.2, w + 0.06, 0.07, 0.04, tb);
      for (const s0 of [-1, 1]) this.b(F, s0 * (w * 0.5), 0.3 + g + (H - g) * 0.5, fz + 0.21, 0.08, H - g, 0.04, tb);
      for (let i = 0; i < 3; i++) this.b(F, (-0.3 + i * 0.3) * w, 0.3 + g + (H - g) * 0.5, fz + 0.215, 0.05, (H - g) * 0.9, 0.03, tb, 0, (i - 1) * 0.5);
    } else if (kind === 3) {
      // The workshop: a wide opening with its boards across, an awning on brackets, a sign on an iron arm.
      this.b(F, 0, 0.3 + H * 0.5, 0, w, H, d, plaster);
      this.b(F, -w * 0.12, 0.3 + 0.32, fz + 0.01, w * 0.58, 0.56, 0.03, paint);
      for (let i = 0; i < 4; i++) this.b(F, -w * 0.12, 0.3 + 0.1 + i * 0.14, fz + 0.025, w * 0.6, 0.02, 0.02, DARK);
      this.b(F, -w * 0.12, 0.3 + 0.62, fz + 0.03, w * 0.66, 0.07, 0.07, tb);
      this.b(F, -w * 0.12, 0.3 + 0.82, fz + 0.2, w * 0.74, 0.04, 0.36, '#4a4038', 0.42);
      this.b(F, -w * 0.12, 0.3 + 0.86, fz + 0.2, w * 0.7, 0.04, 0.32, SNOW[0] as string, 0.42);
      for (const s0 of [-1, 1]) this.b(F, -w * 0.12 + s0 * w * 0.34, 0.3 + 0.76, fz + 0.12, 0.03, 0.03, 0.26, '#22252a', -0.6);
      // The sign: an iron arm out from the wall, a board hanging under it.
      const sx = w * 0.42;
      this.b(F, sx, 0.3 + 1.02, fz + 0.2, 0.03, 0.03, 0.42, '#22252a');
      this.b(F, sx, 0.3 + 0.86, fz + 0.34, 0.04, 0.24, 0.3, k > 0.5 ? '#5a2a24' : '#3a3026');
      this.b(F, sx, 0.3 + 0.86, fz + 0.34, 0.05, 0.12, 0.14, '#8a8f98');
      this.b(F, sx, 0.3 + 0.99, fz + 0.34, 0.06, 0.03, 0.3, SNOW[1] as string);
      for (const s0 of [-1, 1]) this.b(F, s0 * (w * 0.5), 0.3 + H * 0.5, fz + 0.01, 0.09, H, 0.04, tb);
    } else {
      this.b(F, 0, 0.3 + H * 0.5, 0, w, H, d, wall);
      if (kind === 1) {
        // Dressed corners, lighter, block over block; a band of stone at the floor.
        for (const s0 of [-1, 1])
          for (let i = 0; i < Math.floor(H / 0.22); i++) this.b(F, s0 * (w * 0.5 - (i % 2 ? 0.07 : 0.1)), 0.3 + 0.11 + i * 0.22, fz + 0.01, i % 2 ? 0.16 : 0.22, 0.2, 0.04, QUOIN);
        if (storeys > 1) this.b(F, 0, 0.3 + 0.82, fz + 0.02, w, 0.06, 0.05, QUOIN);
      } else {
        // Timbers: corner posts, a beam at each floor, braces.
        for (const s0 of [-1, 1]) this.b(F, s0 * (w * 0.5), 0.3 + H * 0.5, fz + 0.01, 0.1, H, 0.04, tb);
        for (let f = 0; f <= storeys; f++) this.b(F, 0, 0.3 + Math.min(H - 0.04, 0.04 + f * 0.56), fz + 0.015, w, 0.07, 0.035, tb);
        for (const s0 of [-1, 1]) this.b(F, s0 * w * 0.3, 0.3 + 0.86, fz + 0.02, 0.06, 0.5, 0.03, tb, 0, s0 * 0.7);
      }
    }
    // The door (to one side, or in the middle of a stone house), and the windows.
    const doorX = kind === 1 ? 0 : kind === 3 ? w * 0.34 : (k > 0.5 ? -1 : 1) * w * 0.26;
    const doorZ = fz + (kind === 4 ? 0 : 0);
    if (kind !== 3) this.door(F, doorX, doorZ, 0.26, 0.5, paint, kind === 1 ? QUOIN : tb, kind === 1);
    else this.door(F, doorX, doorZ, 0.22, 0.46, paint, tb, false);
    const warm = lit && k2 > 0.55;
    const litCol = lit ? (warm ? '#ffcf9e' : '#c8d6ee') : null;
    const winZ = fz + (kind === 4 ? 0.22 : 0);
    const upper = 0.3 + (storeys > 1 || kind === 4 ? 1.12 : 0.72);
    const ww = kind === 1 ? 0.16 : 0.2;
    if (kind === 1 || kind === 2 || kind === 4) {
      // Upstairs: two windows (one in a narrow stone house), one lit.
      const xs = kind === 1 ? [-w * 0.26, w * 0.26] : [-w * 0.24, w * 0.24];
      xs.forEach((x, i) => this.window(F, x, upper, winZ, ww, 0.26, paint, k > 0.7 && i === 0, lit && i === 1 ? litCol : null));
      if (kind !== 4 && storeys > 1) this.window(F, -doorX * 0.9, 0.3 + 0.52, fz, ww, 0.24, paint, false, null);
    } else if (kind === 0) {
      this.window(F, -doorX, 0.3 + 0.55, fz, ww, 0.26, paint, false, null);
      if (storeys > 1) [-w * 0.24, w * 0.24].forEach((x, i) => this.window(F, x, upper, winZ, ww, 0.26, paint, k > 0.8 && i === 0, lit && i === 1 ? litCol : null));
      else this.window(F, doorX, upper - 0.08, winZ, ww * 0.9, 0.2, paint, false, litCol);
    } else {
      if (storeys > 1) this.window(F, 0, upper, winZ, ww * 1.2, 0.26, paint, true, litCol);
    }
    if (lit) {
      const wx = kind === 3 ? 0 : kind === 0 && storeys === 1 ? doorX : w * 0.24;
      this.c.light(F.at(wx, upper, winZ + 0.12), warm ? WARM : COLD, 0.9, warm ? 0.5 : 0.42, 0.5);
    }
    // A gable with its hoist beam and its loft door (the tall gable fronts).
    const top = 0.3 + H;
    const gable = kind === 2 || (kind === 1 && k > 0.62);
    const snowy = 0.45 + k * 0.55;
    if (gable) {
      this.roof(F, w, d, top, k, true, wall, snowy);
      if (kind === 2) {
        this.b(F, 0, top + 0.26, fz + 0.03, 0.18, 0.26, 0.03, paint);
        this.b(F, 0, top + 0.5, fz + 0.18, 0.06, 0.06, 0.36, tb);
        this.b(F, 0, top + 0.44, fz + 0.34, 0.012, 0.12, 0.012, '#888c92');
      }
      this.icicles(F, -w * 0.45, -w * 0.12, top + 0.02, fz + 0.1, 3, k);
      this.icicles(F, w * 0.12, w * 0.45, top + 0.02, fz + 0.1, 3, k + 0.3);
    } else {
      this.roof(F, w, d, top, k, false, wall, snowy);
      const rise = Math.tan(0.62) * d * 0.5;
      this.sideGables(F, w, d, top, rise, wall);
      this.icicles(F, -w * 0.5, w * 0.5, top - 0.02, fz + 0.12 + (kind === 4 ? 0.1 : 0), 6, k);
      // A dormer on the tall ones.
      if (storeys > 1 && k > 0.55) {
        this.b(F, -w * 0.05, top + 0.22, fz - 0.12, 0.26, 0.24, 0.3, wall);
        this.b(F, -w * 0.05, top + 0.22, fz + 0.03, 0.14, 0.14, 0.02, litCol && k2 > 0.8 ? litCol : DARK);
        this.kit.add(this.prism, ROOF[Math.floor(k2 * 5.99)] as string, F.m(-w * 0.05, top + 0.34, fz - 0.12, v(0.34, 0.16, 0.36)));
        this.kit.add(this.prism, SNOW[0] as string, F.m(-w * 0.05, top + 0.36, fz - 0.14, v(0.3, 0.14, 0.34)));
      }
    }
    // A chimney now and then (on the side away from the street), its cap snowed on.
    if (k > 0.35) {
      const cx = (k > 0.65 ? -1 : 1) * w * 0.3;
      const ch = top + 0.4 + k * 0.3;
      this.b(F, cx, ch, -d * 0.18, 0.2, 0.62 + k * 0.3, 0.2, '#50545b');
      this.b(F, cx, ch + 0.33 + k * 0.15, -d * 0.18, 0.26, 0.05, 0.26, '#3c4046');
      this.b(F, cx, ch + 0.37 + k * 0.15, -d * 0.18, 0.24, 0.04, 0.24, SNOW[1] as string);
    }
    // By the door: a woodpile under snow, a barrel, a bench; and the drift against the front.
    const by = -doorX * 0.9;
    const thing = Math.floor(k2 * 97) % 4;
    if (thing === 0) {
      this.b(F, by, 0.3 + 0.16, fz + 0.14, 0.38, 0.32, 0.18, '#3d2f24');
      for (let i = 0; i < 3; i++) this.kit.add(this.cyl, '#6b5440', F.m(by - 0.12 + i * 0.12, 0.3 + 0.1 + (i % 2) * 0.1, fz + 0.235, v(0.09, 0.02, 0.09), Math.PI / 2));
      this.b(F, by, 0.3 + 0.34, fz + 0.14, 0.4, 0.05, 0.2, SNOW[0] as string);
    } else if (thing === 1) {
      this.kit.add(this.cyl, '#3a2e24', F.m(by, 0.3 + 0.14, fz + 0.16, v(0.2, 0.28, 0.2)));
      this.kit.add(this.cyl, SNOW[2] as string, F.m(by, 0.3 + 0.29, fz + 0.16, v(0.19, 0.03, 0.19)));
    } else if (thing === 2) {
      this.b(F, by, 0.3 + 0.14, fz + 0.15, 0.4, 0.04, 0.12, tb);
      for (const s0 of [-1, 1]) this.b(F, by + s0 * 0.16, 0.3 + 0.07, fz + 0.15, 0.04, 0.14, 0.1, tb);
      this.b(F, by, 0.3 + 0.17, fz + 0.15, 0.38, 0.03, 0.11, SNOW[1] as string);
    }
    this.kit.add(this.mound, SNOW[Math.floor(k * 2.99)] as string, F.m((k - 0.5) * w * 0.6, 0.0, fz + 0.06, v(0.5 + k * 0.4, 0.16, 0.22)));
  }

  /** A house seen from behind (the rows going back): a lower body, its roof, a chimney, no front to speak of. */
  back(p: THREE.Vector3, yaw: number, k: number, k2: number): void {
    const F = new Frame(p, yaw);
    const w = S * (0.92 + k * 0.14);
    const d = S * (0.94 + k2 * 0.1);
    const H = 0.5 + Math.floor(k2 * 2.99) * 0.28;
    const wall = k2 > 0.7 ? (STONE[Math.floor(k * 3.99)] as string) : (PLASTER[Math.floor(k * 4.99)] as string);
    this.b(F, 0, 0.2 + H * 0.5, 0, w, H + 0.4, d, wall);
    const gable = k2 > 0.5;
    // (Snowed over almost whole: dark roofs one behind the other read as a sea of the same house.)
    this.roof(F, w, d, 0.4 + H, k, gable, wall, 0.88 + k2 * 0.12);
    if (!gable) this.sideGables(F, w, d, 0.4 + H, Math.tan(0.62) * d * 0.5, wall);
    if (k > 0.5) {
      this.b(F, w * 0.28, 0.4 + H + 0.5, -d * 0.2, 0.2, 0.7, 0.2, '#50545b');
      this.b(F, w * 0.28, 0.4 + H + 0.87, -d * 0.2, 0.24, 0.05, 0.24, SNOW[1] as string);
    }
    if (k2 < 0.3 || k > 0.9) this.window(F, 0, 0.4 + H * 0.6, d * 0.5, 0.18, 0.22, PAINT[Math.floor(k * 4.99)] as string, false, k > 0.9 ? '#ffcf9e' : null);
    if (k > 0.9) this.c.light(F.at(0, 0.4 + H * 0.6, d * 0.5 + 0.15), WARM, 0.8, 0.45, 0.5);
  }

  /** A yard among the backs: snow, a dry-stone wall on its open sides and one thing in it. */
  yard(p: THREE.Vector3, k: number, k2: number, walls: number[]): void {
    const F = new Frame(p, 0);
    this.kit.add(this.mound, SNOW[Math.floor(k * 2.99)] as string, F.m(0, -0.04, 0, v(S * 1.05, 0.14, S * 1.05)));
    for (const dir of walls) {
      const a = (dir * Math.PI) / 3 + Math.PI / 6;
      const W = new Frame(F.at(Math.sin(a) * S * 0.5, 0, Math.cos(a) * S * 0.5), a);
      for (let i = 0; i < 3; i++) {
        W.p.y = p.y;
        this.b(W, -0.4 + i * 0.4, 0.17, 0, 0.42, 0.34, 0.26, STONE[(i + dir) % 4] as string);
        this.b(W, -0.4 + i * 0.4, 0.36, 0, 0.4, 0.06, 0.26, SNOW[i % 3] as string);
      }
    }
    const what = Math.floor(k2 * 5.999);
    if (what === 0) {
      // A well: a ring of stones, two posts, a little roof, the bucket's beam.
      this.kit.add(this.cyl, STONE[1] as string, F.m(0, 0.2, 0, v(0.6, 0.4, 0.6)));
      this.kit.add(this.cyl, '#12151a', F.m(0, 0.41, 0, v(0.44, 0.02, 0.44)));
      this.kit.add(this.cyl, SNOW[0] as string, F.m(0, 0.41, 0, v(0.62, 0.04, 0.62)));
      for (const s0 of [-1, 1]) this.b(F, s0 * 0.3, 0.6, 0, 0.06, 0.8, 0.06, TIMBER[1] as string);
      this.kit.add(this.prism, ROOF[0] as string, F.m(0, 0.98, 0, v(0.8, 0.3, 0.5)));
      this.kit.add(this.prism, SNOW[1] as string, F.m(0, 1.0, 0, v(0.72, 0.28, 0.46)));
    } else if (what === 1 || what === 2) {
      // A bare tree, snow along its boughs.
      const h = 1.3 + k * 0.8;
      this.b(F, 0, h * 0.5, 0, 0.12, h, 0.12, '#2a221c');
      for (let i = 0; i < 4; i++) {
        const a = i * 1.7 + k * 3;
        const y = h * (0.55 + i * 0.12);
        const bx = Math.sin(a) * 0.28;
        const bz = Math.cos(a) * 0.28;
        this.kit.add(this.box, '#2a221c', place(p.x + bx, p.y + y, p.z + bz, a, v(0.05, 0.05, 0.62), 0.6));
        this.kit.add(this.box, SNOW[i % 3] as string, place(p.x + bx, p.y + y + 0.04, p.z + bz, a, v(0.05, 0.025, 0.5), 0.6));
      }
    } else if (what === 3) {
      // A haystack under its snow, its pole.
      this.kit.add(this.cone, '#5a4a32', F.m(0, 0.45, 0, v(0.9, 0.9, 0.9)));
      this.kit.add(this.cone, SNOW[0] as string, F.m(0, 0.72, 0, v(0.6, 0.44, 0.6)));
      this.b(F, 0, 0.8, 0, 0.04, 1.6, 0.04, TIMBER[0] as string);
    } else if (what === 4) {
      // A woodshed: a lean-to roof on posts over stacked logs.
      this.b(F, 0, 0.25, -0.1, 0.8, 0.5, 0.36, '#3d2f24');
      for (let i = 0; i < 4; i++) this.kit.add(this.cyl, '#6b5440', F.m(-0.3 + i * 0.2, 0.15 + (i % 2) * 0.18, 0.09, v(0.14, 0.02, 0.14), Math.PI / 2));
      for (const s0 of [-1, 1]) this.b(F, s0 * 0.42, 0.38, 0.14, 0.05, 0.76, 0.05, TIMBER[1] as string);
      this.b(F, 0, 0.76, 0, 1.0, 0.05, 0.6, ROOF[3] as string, -0.25);
      this.b(F, 0, 0.8, 0, 0.96, 0.05, 0.56, SNOW[1] as string, -0.25);
    }
  }

  /** A little square where a front gives way: its paving under snow, a bench, a bare tree or a wayside cross. */
  plaza(p: THREE.Vector3, yaw: number, k: number): void {
    const F = new Frame(p, yaw);
    this.kit.add(this.cyl, '#5a5f67', F.m(0, 0.02, 0, v(S * 1.0, 0.04, S * 1.0)));
    this.kit.add(this.mound, SNOW[1] as string, F.m(0, 0.0, -S * 0.3, v(S * 0.9, 0.12, S * 0.4)));
    // A low wall behind it, with a stone seat along it.
    for (let i = 0; i < 3; i++) {
      this.b(F, -0.42 + i * 0.42, 0.22, -S * 0.45, 0.44, 0.44, 0.24, STONE[i % 4] as string);
      this.b(F, -0.42 + i * 0.42, 0.46, -S * 0.45, 0.42, 0.06, 0.24, SNOW[i % 3] as string);
    }
    this.b(F, 0.1, 0.16, -S * 0.2, 0.6, 0.06, 0.18, STONE[2] as string);
    for (const s0 of [-1, 1]) this.b(F, 0.1 + s0 * 0.24, 0.07, -S * 0.2, 0.08, 0.14, 0.14, STONE[3] as string);
    this.b(F, 0.1, 0.2, -S * 0.2, 0.58, 0.03, 0.16, SNOW[0] as string);
    if (k > 0.5) {
      // A wayside cross on its steps, a little lamp at its foot (a cold glow: someone keeps it).
      this.b(F, -0.36, 0.08, -0.02, 0.4, 0.16, 0.4, STONE[1] as string);
      this.b(F, -0.36, 0.2, -0.02, 0.28, 0.1, 0.28, STONE[2] as string);
      this.b(F, -0.36, 0.7, -0.02, 0.1, 0.9, 0.1, '#7c818a');
      this.b(F, -0.36, 0.96, -0.02, 0.4, 0.09, 0.09, '#7c818a');
      this.b(F, -0.36, 1.16, -0.02, 0.11, 0.03, 0.11, SNOW[0] as string);
      this.b(F, -0.36, 1.01, -0.02, 0.42, 0.03, 0.11, SNOW[1] as string);
      this.b(F, -0.2, 0.32, 0.12, 0.06, 0.1, 0.06, '#e8eef8');
      this.c.light(F.at(-0.2, 0.34, 0.12), WHITE, 0.7, 0.55, 1);
    } else {
      const h = 1.6;
      this.b(F, -0.32, h * 0.5, 0, 0.13, h, 0.13, '#2a221c');
      for (let i = 0; i < 5; i++) {
        const a = i * 1.3 + k * 4;
        const at = F.at(-0.32 + Math.sin(a) * 0.3, h * (0.55 + i * 0.1), Math.cos(a) * 0.3);
        this.kit.add(this.box, '#2a221c', place(at.x, at.y, at.z, a, v(0.05, 0.05, 0.66), 0.6));
        this.kit.add(this.box, SNOW[i % 3] as string, place(at.x, at.y + 0.04, at.z, a, v(0.05, 0.025, 0.54), 0.6));
      }
      this.kit.add(this.cyl, '#454a52', F.m(-0.32, 0.08, 0, v(0.5, 0.16, 0.5)));
      this.kit.add(this.cyl, SNOW[2] as string, F.m(-0.32, 0.17, 0, v(0.48, 0.03, 0.48)));
    }
  }

  /**
   * The village's arch at the top of the lane (`a` its middle, the lane going through along z): two piers with their
   * plinths, a round arch of voussoirs with its keystone, a little slate roof over it under snow, the shrine's niche
   * with its lamp, icicles along the arch; the old wall running on to each side with its coping.
   */
  arch(a: THREE.Vector3): void {
    const F = new Frame(a, 0);
    const span = 1.5;
    const pierH = 1.9;
    for (const s0 of [-1, 1]) {
      this.b(F, s0 * (span + 0.38), 0.18, 0, 0.92, 0.36, 1.0, '#4f535b');
      this.b(F, s0 * (span + 0.38), 0.36 + pierH * 0.5, 0, 0.76, pierH, 0.86, STONE[1] as string);
      for (let i = 0; i < 4; i++) this.b(F, s0 * (span + 0.38 + (i % 2 ? 0.06 : -0.06)), 0.5 + i * 0.44, 0.44, 0.5, 0.2, 0.03, QUOIN);
      this.b(F, s0 * (span + 0.38), 0.36 + pierH + 0.06, 0, 0.86, 0.12, 0.94, QUOIN);
      // The wall going on, its coping snowed on, a buttress.
      for (let i = 0; i < 3; i++) {
        const x = s0 * (span + 1.2 + i * 0.9);
        this.b(F, x, 0.9, 0, 0.92, 1.8 - i * 0.1, 0.62, STONE[(i + 2) % 4] as string);
        this.b(F, x, 1.82 - i * 0.05, 0, 0.94, 0.1, 0.7, '#4f535b');
        this.b(F, x, 1.9 - i * 0.05, 0, 0.96, 0.07, 0.72, SNOW[i % 3] as string);
        this.icicles(F, x - 0.4, x + 0.4, 1.77 - i * 0.05, 0.36, 3, i * 0.37);
      }
    }
    // The arch: eleven voussoirs round a half circle, the keystone paler and proud.
    const R = span + 0.02;
    const y0 = 0.36 + pierH;
    const n = 11;
    for (let i = 0; i < n; i++) {
      const t = Math.PI * ((i + 0.5) / n);
      const x = -Math.cos(t) * (R + 0.18);
      const y = y0 + Math.sin(t) * (R + 0.18);
      const key = i === (n - 1) / 2;
      this.b(F, x, y, 0, key ? 0.34 : 0.3, key ? 0.5 : 0.4, key ? 1.0 : 0.9, key ? QUOIN : (STONE[i % 4] as string), 0, t - Math.PI / 2);
    }
    // The spandrels filled up to the top, a cornice, the little roof.
    for (const s0 of [-1, 1]) this.b(F, s0 * (R * 0.75 + 0.2), y0 + R * 0.75, 0, R * 0.55, R * 0.62, 0.84, STONE[2] as string);
    const top = y0 + R + 0.42;
    this.b(F, 0, top, 0, 2 * span + 1.8, 0.14, 1.0, QUOIN);
    this.b(F, 0, top + 0.22, 0, 2 * span + 1.6, 0.3, 0.84, STONE[0] as string);
    const roofY = top + 0.37;
    for (const s0 of [-1, 1]) {
      this.b(F, 0, roofY + 0.18, s0 * 0.3, 2 * span + 2.0, 0.08, 0.78, ROOF[0] as string, s0 * 0.55);
      this.b(F, 0, roofY + 0.25, s0 * 0.24, 2 * span + 1.9, 0.07, 0.6, SNOW[s0 > 0 ? 0 : 2] as string, s0 * 0.55);
    }
    this.b(F, 0, roofY + 0.4, 0, 2 * span + 2.0, 0.09, 0.16, SNOW[0] as string);
    this.icicles(F, -span - 0.8, span + 0.8, roofY - 0.02, 0.56, 12, 0.4);
    this.icicles(F, -span + 0.2, span - 0.2, y0 - 0.02, 0.46, 4, 0.7);
    // The shrine's niche over the keystone, its lamp lit: the last light before the road.
    this.b(F, 0, top + 0.22, 0.43, 0.32, 0.26, 0.04, '#10131a');
    this.kit.add(this.prism, QUOIN, F.m(0, top + 0.35, 0.44, v(0.42, 0.14, 0.06)));
    this.b(F, 0, top + 0.2, 0.47, 0.1, 0.14, 0.06, '#e8eef8');
    this.c.light(F.at(0, top + 0.22, 0.55), WHITE, 1.4, 0.8, 1);
    // A carved sign on each pier (the village's mark: the moss and the mountain).
    for (const s0 of [-1, 1]) {
      this.kit.add(this.prism, '#3d4148', F.m(s0 * (span + 0.38), 1.4, 0.445, v(0.34, 0.32, 0.02)));
      this.b(F, s0 * (span + 0.38), 1.36, 0.45, 0.4, 0.03, 0.02, '#3d4148');
    }
    // Drifts banked against the piers.
    for (const s0 of [-1, 1]) this.kit.add(this.mound, SNOW[1] as string, F.m(s0 * (span + 0.1), 0, 0.5, v(0.9, 0.3, 0.5)));
  }
}
